import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

/**
 * Halftime overlay — shown between the two halves of a Kickard match.
 *
 *   • Displays the cumulative score (you vs bot).
 *   • Auto-advances to the second half after AUTO_MS (3 s) so the player can
 *     read the score without action.
 *   • Tap-to-skip: tapping the overlay before AUTO_MS still respects a 500 ms
 *     minimum visibility (so the score is always at least briefly readable).
 *   • Calls onContinue exactly once via donePostedRef guard.
 */
const AUTO_MS = 10000;
const MIN_VISIBLE_MS = 500;

export default function HalftimeOverlay({ scoreP1, scoreP2, nextStarter, onContinue, isOnline = false, myPlayerId = 'p1', meName, opponentName }) {
  const [skipping, setSkipping] = useState(false);
  const [remainingMs, setRemainingMs] = useState(AUTO_MS);
  const donePostedRef = useRef(false);
  const startedAtRef = useRef(Date.now());

  // Compute "my" vs "their" score from the local player's perspective.
  const myScore = myPlayerId === 'p1' ? scoreP1 : scoreP2;
  const oppScore = myPlayerId === 'p1' ? scoreP2 : scoreP1;
  // Default labels for local bot mode: "You" / "Bot". Online uses names.
  const myLabel = isOnline ? (meName || 'You') : 'You';
  const oppLabel = isOnline ? (opponentName || 'Opponent') : 'Bot';
  // "Next starter" sentence — neutral in online (NEVER says "bot").
  const meStartsNext = (nextStarter === myPlayerId);
  const nextStarterText = isOnline
    ? (meStartsNext ? 'You start the 2nd half' : `${oppLabel} starts the 2nd half`)
    : (nextStarter === 'p1' ? 'You start the 2nd half' : 'Bot starts the 2nd half');

  useEffect(() => {
    startedAtRef.current = Date.now();
    const t = setTimeout(() => {
      if (donePostedRef.current) return;
      donePostedRef.current = true;
      onContinue?.();
    }, AUTO_MS);
    // Tick countdown every 250 ms for a smooth visible counter.
    const i = setInterval(() => {
      const left = AUTO_MS - (Date.now() - startedAtRef.current);
      setRemainingMs(Math.max(0, left));
      if (left <= 0) clearInterval(i);
    }, 250);
    return () => { clearTimeout(t); clearInterval(i); };
  }, [onContinue]);

  const secondsLeft = Math.max(1, Math.ceil(remainingMs / 1000));

  const handleSkip = () => {
    if (donePostedRef.current) return;
    if (skipping) return;
    setSkipping(true);
    const elapsed = Date.now() - startedAtRef.current;
    const wait = Math.max(0, MIN_VISIBLE_MS - elapsed);
    setTimeout(() => {
      if (donePostedRef.current) return;
      donePostedRef.current = true;
      onContinue?.();
    }, wait);
  };

  return (
    <motion.div
      key="halftime"
      data-testid="halftime-overlay"
      onClick={handleSkip}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="absolute inset-0 z-40 bg-black/88 backdrop-blur-sm flex flex-col items-center justify-center cursor-pointer select-none"
    >
      <motion.div
        initial={{ scale: 0.7, opacity: 0, letterSpacing: '0.6em' }}
        animate={{ scale: 1, opacity: 1, letterSpacing: '0.12em' }}
        transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
        className="font-heading uppercase"
        style={{
          fontSize: 52,
          color: 'hsl(var(--k-gold-bright))',
          textShadow: '0 0 28px hsl(var(--k-gold) / 0.85)',
        }}
      >
        HALFTIME
      </motion.div>

      {/* Gold rule under HALFTIME — broadcast panel style */}
      <motion.div
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ scaleX: 1, opacity: 1 }}
        transition={{ delay: 0.35, duration: 0.45 }}
        className="mt-3 h-[2px] w-28 origin-center"
        style={{
          background:
            'linear-gradient(90deg, transparent 0%, hsl(var(--k-gold-bright)) 50%, transparent 100%)',
          boxShadow: '0 0 10px hsl(var(--k-gold) / 0.65)',
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.45 }}
        className="mt-6 flex items-center gap-8"
      >
        <div className="flex flex-col items-center">
          <span className="font-mono text-[10px] tracking-[0.25em] text-white/55 uppercase truncate max-w-[120px]">{myLabel}</span>
          <span data-testid="halftime-score-p1" className="font-heading text-5xl" style={{ color: 'hsl(var(--k-gold-bright))' }}>{myScore}</span>
        </div>
        <div className="font-heading text-3xl text-white/35">·</div>
        <div className="flex flex-col items-center">
          <span className="font-mono text-[10px] tracking-[0.25em] text-white/55 uppercase truncate max-w-[120px]">{oppLabel}</span>
          <span data-testid="halftime-score-p2" className="font-heading text-5xl" style={{ color: 'hsl(var(--k-red))' }}>{oppScore}</span>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.4 }}
        className="mt-8 font-body text-xs tracking-[0.3em] text-white/65 uppercase"
      >
        {nextStarterText}
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.4 }}
        className="mt-4 font-heading text-2xl text-[hsl(var(--k-gold))] tracking-[0.16em]"
        data-testid="halftime-countdown"
      >
        Second half starts in {secondsLeft}
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.55 }}
        transition={{ delay: 1.2, duration: 0.5 }}
        className="mt-6 font-mono text-[10px] tracking-[0.3em] text-white/45 uppercase"
      >
        Tap to start now
      </motion.div>
    </motion.div>
  );
}
